import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ReactComponent as DatePickerIcon } from 'assets/icons/svg/datepicker-icon.svg';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { CornerStyle } from 'context/ThemeContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import './DatePicker.css';
import dayjs from 'dayjs';

type DatePickerProps = {
  label: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  selectedDate: Date;
  errorMessage?: string;
  changeDate(date: Date): void;
};

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  errorMessage,
  changeDate,
  disabled,
  minDate,
  maxDate,
  label,
}) => {
  const { brandTheme, inputStyle } = useThemeContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'datePickerComponent',
  });
  const [error, setError] = useState<string>('');

  const getCornerStyle = () => {
    switch (inputStyle) {
      case CornerStyle.FULL_ROUND:
        return 'rounded-full';
      case CornerStyle.ROUNDED_CORNERS:
        return 'rounded-[10px]';
      case CornerStyle.SQUARE_CORNERS:
        return 'rounded-none';
      default:
        return 'rounded-full';
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      setError('');
      return;
    }

    // Validate min date
    if (minDate && date < minDate) {
      setError(errorMessage || 'Date is before minimum allowed date');
      return;
    }

    // Validate max date
    if (maxDate && date > maxDate) {
      setError(errorMessage || 'Date is after maximum allowed date');
      return;
    }

    setError('');
    changeDate(date);
  };

  const formatDisplayDate = (date: Date) => {
    if (!date) return '';
    return dayjs(date).format('MM/DD/YYYY');
  };

  return (
    <div className={`relative w-full ${disabled ? 'opacity-70' : ''}`}>
      <div
        className={`
        date-picker-wrapper
        relative w-full h-input flex items-center text-[0.9rem] duration-75 border-2 border-solid border-input
        ${disabled ? 'pointer-events-none' : ''}
        ${getCornerStyle()}
      `}
        style={{
          // Apply brand theme to the wrapper for custom styling
          ['--brand-theme' as any]: brandTheme,
        }}
      >
        <span className='z-40 absolute left-6 text-muted transition-all duration-300 pointer-events-none text-xxxs px-0.5 bg-white -translate-y-[25px] opacity-100'>
          {label}
        </span>
        <div
          className='relative w-full flex items-center'
          style={{ padding: '0 0.5rem 0 1.5rem' }}
        >
          <ReactDatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
            dateFormat='MM/dd/yyyy'
            showPopperArrow={false}
            calendarClassName='custom-calendar'
            className='w-full bg-transparent border-none outline-none font-inherit text-[0.9rem] cursor-pointer'
            wrapperClassName='w-full'
            popperClassName='custom-popper'
            aria-label={t('ariaChangeDate')}
            placeholderText={formatDisplayDate(selectedDate)}
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
          />
        </div>
        <div className='absolute right-4 pointer-events-none'>
          <DatePickerIcon width={20} height={20} />
        </div>
      </div>
      {error && (
        <span className='absolute -bottom-5 left-0 text-xs text-red-600'>
          {error}
        </span>
      )}
    </div>
  );
};

export default DatePicker;
